import React, { useEffect, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useDataScope } from "@/hooks/useDataScope";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate } from "@/lib/format";
import { PO_STATUS_META, PO_STATUS_LABEL } from "@/lib/inventory";
import POWizard from "@/components/POWizard";
import POPhotos from "@/components/POPhotos";
import { Package, Plus, Loader2, Layers, ChevronDown, ChevronRight, Download, FileSpreadsheet, Boxes, Search, MapPin, RefreshCw } from "lucide-react";

function StatusPill({ s }) {
  const m = PO_STATUS_META[s] || { label: s, cls: "bg-slate-100 text-slate-600" };
  return <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${m.cls}`}>{m.label}</span>;
}

/** How much of what ShipStation shipped we could actually place against a SKU. */
function MatchReport({ m }) {
  const clean = m.unmatched_units === 0;
  return (
    <div data-testid="stock-match-report" className={`rounded-xl border px-4 py-3 mb-3 text-sm ${clean ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-300 text-amber-900"}`}>
      <p className="font-semibold">
        {m.matched_units.toLocaleString()} of {m.shipped_units.toLocaleString()} shipped units matched a SKU we hold ({m.match_pct ?? 0}%)
      </p>
      <p className="text-xs mt-1 opacity-90">
        {m.shipments_scanned.toLocaleString()} shipments across {m.stores_scanned} store{m.stores_scanned === 1 ? "" : "s"} since {m.since}.
        Units are counted per line, so three of the same SKU on one order counts as three.
        {!clean && ` ${m.unmatched_units.toLocaleString()} units shipped as something we don't have on file, so on hand may read high for those products.`}
      </p>
      {!clean && m.unmatched_sample?.length > 0 && (
        <p className="text-xs mt-1.5" data-testid="stock-unmatched-sample">
          <span className="font-semibold">Not on file:</span> {m.unmatched_sample.join(" · ")}
        </p>
      )}
    </div>
  );
}

export default function Inventory() {
  const { param, allMode, accountId } = useDataScope();
  const { user } = useAuth();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [wizard, setWizard] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [tab, setTab] = useState("pos"); // 'pos' | 'stock'
  const [stock, setStock] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockErr, setStockErr] = useState(false);
  const [stockQuery, setStockQuery] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [merging, setMerging] = useState(false);

  const effectiveAccount = accountId || user?.account_id;

  const mergeBins = async () => {
    if (!effectiveAccount) return;
    if (!window.confirm("Merge duplicate bin codes into one canonical code (e.g. GC1 + G-C1 → G-C1), summing quantities? This can't be undone.")) return;
    setMerging(true);
    try {
      const { data } = await api.post("/inventory/stock/normalize-locations", { account_id: effectiveAccount });
      toast.success(`Cleaned up: ${data.balances_before} → ${data.balances_after} bins`);
      loadStock();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMerging(false); }
  };

  const loadStock = (reconcile = false) => {
    if (!effectiveAccount) return;
    if (reconcile) setReconciling(true); else setStockLoading(true);
    setStockErr(false);
    api.get(`/inventory/stock/all?account_id=${effectiveAccount}${reconcile ? "&reconcile=true" : ""}`)
      .then(({ data }) => setStock(data))
      .catch(() => setStockErr(true))
      .finally(() => { setStockLoading(false); setReconciling(false); });
  };
  useEffect(() => {
    if (tab === "stock" && !allMode && !stock) loadStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, allMode, param]);

  const load = () => {
    setLoading(true); setError(false);
    const qs = param ? `?${param}` : "";
    api.get(`/inventory/pos${qs}`)
      .then(({ data }) => setPos(data.pos || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); setStock(null); }, [param]);

  const toggle = async (po) => {
    if (expanded === po.id) { setExpanded(null); return; }
    setExpanded(po.id);
    if (!detail[po.id]) {
      const { data } = await api.get(`/inventory/pos/${po.id}`);
      let summary = null;
      if (["receiving", "discrepancy_review", "completed"].includes(po.status)) {
        try { const r = await api.get(`/inventory/pos/${po.id}/receiving-summary`); summary = r.data; } catch (err) { console.debug("receiving summary unavailable", err?.message || err); }
      }
      setDetail((d) => ({ ...d, [po.id]: { ...data, _summary: summary } }));
    }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="inline-block text-xs uppercase tracking-wider font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded">Inventory</span>
          <h1 className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Manrope" }} data-testid="inventory-title">
            {tab === "stock" ? "On-hand stock" : "Inbound POs"}
          </h1>
          <p className="text-slate-500 mt-1">
            {tab === "stock"
              ? "What you're holding with us right now — received units, less what ShipStation has shipped."
              : "Upload a purchase order before your inventory arrives and follow it through receiving."}
          </p>
        </div>
        {!allMode && (
          <div className="flex items-center gap-2">
            <button onClick={() => window.open(`${API_BASE}/inventory/pos/template`, "_blank", "noopener")} data-testid="po-template-btn" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2.5 rounded-md">
              <Download className="w-4 h-4" /> Download template
            </button>
            <button onClick={() => setWizard(true)} data-testid="new-po-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2.5 rounded-md">
              <Plus className="w-4 h-4" /> New inbound PO
            </button>
          </div>
        )}
      </div>

      {!allMode && (
        <div className="flex items-center gap-1 border-b border-slate-200" data-testid="inventory-tabs">
          <button onClick={() => setTab("pos")} data-testid="inv-tab-pos" className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "pos" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>Inbound POs</button>
          <button onClick={() => setTab("stock")} data-testid="inv-tab-stock" className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === "stock" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}><Boxes className="w-4 h-4" /> On-hand stock</button>
        </div>
      )}

      {tab === "stock" && !allMode && (
        <div data-testid="inventory-stock">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              {stock && <span><span className="font-semibold text-slate-700">{stock.count}</span> SKUs · {stock.reconciled ? <><span className="font-semibold text-slate-700">{stock.total_on_hand}</span> on hand after <span className="font-semibold text-slate-700">{stock.total_sold}</span> units shipped</> : <><span className="font-semibold text-slate-700">{stock.total_units}</span> units received · {stock.located_count} with a location</>}</span>}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={stockQuery} onChange={(e) => setStockQuery(e.target.value)} data-testid="stock-search" placeholder="Search SKU, name, UPC…" className="pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm w-64" />
              </div>
              <button onClick={mergeBins} disabled={merging} data-testid="stock-merge-bins" className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md">{merging ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Merge bins</button>
              <button onClick={() => loadStock(true)} disabled={reconciling} data-testid="stock-reconcile" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-2 rounded-md">
                {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reconcile with ShipStation
              </button>
              {effectiveAccount && (
                <button onClick={() => window.open(`${API_BASE}/inventory/export/shipstation?account_id=${effectiveAccount}&format=xlsx`, "_blank", "noopener")} data-testid="stock-export" className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2 rounded-md"><FileSpreadsheet className="w-4 h-4" /> ShipStation export</button>
              )}
            </div>
          </div>
          {stock?.reconciled && stock.match && <MatchReport m={stock.match} />}
          {stockLoading ? (
            <div className="flex items-center gap-2 text-slate-500 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading stock…</div>
          ) : stockErr ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center"><p className="text-sm font-semibold text-red-700">Couldn&apos;t load stock.</p><button onClick={loadStock} data-testid="stock-retry" className="mt-2 text-sm font-semibold text-blue-600">Retry</button></div>
          ) : !stock || stock.products.length === 0 ? (
            <div data-testid="stock-empty" className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4"><Boxes className="w-7 h-7" /></div>
              <p className="text-lg font-semibold text-slate-700">No on-hand stock yet</p>
              <p className="text-sm text-slate-500 mt-1">Receive a PO or import your existing stock to see it here.</p>
            </div>
          ) : (() => {
            const q = stockQuery.trim().toLowerCase();
            const rows = q ? stock.products.filter((p) => `${p.sku} ${p.name} ${p.barcode}`.toLowerCase().includes(q)) : stock.products;
            return (
              <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="stock-table">
                <table className="w-full text-left text-sm">
                  <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="py-3 px-4 font-medium">Product</th><th className="py-3 px-4 font-medium">SKU</th><th className="py-3 px-4 font-medium">UPC / Barcode</th>
                    {stock.reconciled && <th className="py-3 px-4 font-medium">Received</th>}
                    {stock.reconciled && <th className="py-3 px-4 font-medium">Shipped</th>}
                    <th className="py-3 px-4 font-medium">On hand</th><th className="py-3 px-4 font-medium">Locations</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((p) => (
                      <tr key={p.sku} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`stock-row-${p.sku}`}>
                        <td className="py-3 px-4 text-slate-700">{p.name}</td>
                        <td className="py-3 px-4 font-mono text-xs">{p.sku}</td>
                        <td className="py-3 px-4 font-mono text-xs text-slate-500">{p.barcode || "—"}</td>
                        {stock.reconciled && <td className="py-3 px-4 tabular-nums text-slate-600">{p.received}</td>}
                        {stock.reconciled && <td className="py-3 px-4 tabular-nums text-slate-600">{p.tracked ? p.sold : <span className="text-slate-300" title="Not tracked in the tool yet — sales not deducted">—</span>}</td>}
                        <td className="py-3 px-4 font-semibold text-slate-800 tabular-nums">{stock.reconciled ? p.on_hand : p.total}{stock.reconciled && !p.tracked && <span className="ml-1.5 text-[10px] font-medium text-amber-600 uppercase">untracked</span>}</td>
                        <td className="py-3 px-4">
                          {p.locations.length === 0 ? <span className="text-slate-400">—</span> : (
                            <div className="flex flex-wrap gap-1.5">
                              {p.locations.map((c) => <span key={c} className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-mono"><MapPin className="w-3 h-3 text-slate-400" />{c}</span>)}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}


      {(allMode || tab === "pos") && (allMode ? (
        <div data-testid="inventory-all-mode" className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-4 text-sm text-blue-800">
          <Layers className="w-5 h-5 shrink-0 mt-0.5" /> Pick a specific store from the top bar to upload and view its POs.
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading POs…</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center"><p className="text-sm font-semibold text-red-700">Couldn&apos;t load your POs.</p><button onClick={load} data-testid="inventory-retry" className="mt-2 text-sm font-semibold text-blue-600">Retry</button></div>
      ) : pos.length === 0 ? (
        <div data-testid="inventory-empty" className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4"><Package className="w-7 h-7" /></div>
          <p className="text-lg font-semibold text-slate-700">No inbound POs yet</p>
          <p className="text-sm text-slate-500 mt-1">Upload your first purchase order to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="inventory-po-list">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-3 px-4 font-medium"></th><th className="py-3 px-4 font-medium">Reference</th><th className="py-3 px-4 font-medium">PO #</th><th className="py-3 px-4 font-medium">Vendor</th><th className="py-3 px-4 font-medium">Lines</th><th className="py-3 px-4 font-medium">Units</th><th className="py-3 px-4 font-medium">Status</th><th className="py-3 px-4 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {pos.map((po) => (
                <React.Fragment key={po.id}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => toggle(po)} data-testid={`po-row-${po.id}`}>
                    <td className="py-3 px-4">{expanded === po.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}</td>
                    <td className="py-3 px-4 font-mono font-semibold text-slate-900">{po.boxhero_ref}</td>
                    <td className="py-3 px-4 text-slate-600">{po.customer_po_number || "—"}</td>
                    <td className="py-3 px-4 text-slate-600">{po.vendor || "—"}</td>
                    <td className="py-3 px-4 text-slate-600">{po.line_count}</td>
                    <td className="py-3 px-4 text-slate-600">{po.expected_units}</td>
                    <td className="py-3 px-4"><StatusPill s={po.status} /></td>
                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap">{formatDate(po.created_at)}</td>
                  </tr>
                  {expanded === po.id && (
                    <tr><td colSpan={8} className="bg-slate-50/60 px-6 py-4" data-testid={`po-detail-${po.id}`}>
                      {!detail[po.id] ? <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (() => {
                        const d = detail[po.id];
                        const sm = d._summary;
                        const started = ["receiving", "discrepancy_review", "completed"].includes(po.status);
                        const lg = sm?.line_good || {}, lh = sm?.line_hold || {};
                        return (
                          <div className="space-y-3">
                            {started && sm && (
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 font-semibold px-2.5 py-1 rounded-full" data-testid={`po-cust-good-${po.id}`}>Good: {sm.tallies?.good || 0}</span>
                                  <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 font-semibold px-2.5 py-1 rounded-full" data-testid={`po-cust-held-${po.id}`}>Held: {sm.tallies?.hold || 0}</span>
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => window.open(`${API_BASE}/inventory/pos/${po.id}/receiving-report?format=csv`, "_blank", "noopener")} data-testid={`po-report-csv-${po.id}`} className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"><Download className="w-3.5 h-3.5" /> Report CSV</button>
                                  <button onClick={() => window.open(`${API_BASE}/inventory/pos/${po.id}/receiving-report?format=xlsx`, "_blank", "noopener")} data-testid={`po-report-xlsx-${po.id}`} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"><FileSpreadsheet className="w-3.5 h-3.5" /> XLSX</button>
                                  <button onClick={() => window.open(`${API_BASE}/inventory/pos/${po.id}/reconciliation?format=xlsx`, "_blank", "noopener")} data-testid={`po-recon-${po.id}`} className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 rounded-md"><FileSpreadsheet className="w-3.5 h-3.5" /> Review sheet</button>
                                </div>
                              </div>
                            )}
                            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`po-photos-block-${po.id}`}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">What&apos;s coming</p>
                                  <POPhotos accountId={po.account_id} poId={po.id} kind="inbound"
                                    photos={d.photos || []} onChange={(ph) => setDetail((s) => ({ ...s, [po.id]: { ...s[po.id], photos: ph } }))} />
                                </div>
                                <div>
                                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">What arrived at the warehouse</p>
                                  <POPhotos accountId={po.account_id} poId={po.id} kind="receiving"
                                    photos={d.photos || []} onChange={(ph) => setDetail((s) => ({ ...s, [po.id]: { ...s[po.id], photos: ph } }))} />
                                </div>
                              </div>
                            </div>
                            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                              <table className="w-full text-left text-sm">
                                <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200"><th className="py-2 px-3 font-medium">Product</th><th className="py-2 px-3 font-medium">SKU</th><th className="py-2 px-3 font-medium">Expected</th><th className="py-2 px-3 font-medium">Received</th>{started && sm && <><th className="py-2 px-3 font-medium">Good</th><th className="py-2 px-3 font-medium">Held</th><th className="py-2 px-3 font-medium">Variance</th></>}</tr></thead>
                                <tbody>
                                  {d.lines.map((l) => {
                                    const exp = l.expected_qty || 0, rec = l.received_qty || 0;
                                    const short = Math.max(0, exp - rec), over = Math.max(0, rec - exp);
                                    return (
                                      <tr key={l.id} className="border-b border-slate-50" data-testid={`po-cust-line-${l.id}`}>
                                        <td className="py-2 px-3 text-slate-700">{l.product_title || "—"}{l.variant_title ? ` · ${l.variant_title}` : ""}</td>
                                        <td className="py-2 px-3 font-mono text-xs">{l.sku}</td>
                                        <td className="py-2 px-3 text-slate-700">{exp}</td>
                                        <td className="py-2 px-3 font-semibold text-slate-800">{rec}</td>
                                        {started && sm && <>
                                          <td className="py-2 px-3 text-emerald-700">{lg[l.id] || 0}</td>
                                          <td className="py-2 px-3 text-red-600">{lh[l.id] || 0}</td>
                                          <td className="py-2 px-3">{short ? <span className="text-amber-700 font-semibold">-{short} short</span> : over ? <span className="text-orange-600 font-semibold">+{over} over</span> : <span className="text-emerald-600">✓</span>}</td>
                                        </>}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {wizard && effectiveAccount && <POWizard accountId={effectiveAccount} onClose={() => setWizard(false)} onCreated={load} />}
    </div>
  );
}
